import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { ArrowUpRight, Sprout } from "lucide-react";
import { HeroDecor } from "./Decor";
import { EASE, JOB_PORTAL_URL } from "./constants";

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: EASE },
});

const MaskLine = ({ children, delay, reduce }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={reduce ? false : { y: "110%", rotate: 2 }}
      animate={{ y: "0%", rotate: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 40, damping: 18 });
  const x = useTransform(sx, (v) => v * 24);
  const xInv = useTransform(sx, (v) => v * -24);
  const rotY = useTransform(sx, (v) => v * 4);

  const onMove = (e) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
  };

  return (
    <section
      onMouseMove={onMove}
      onMouseLeave={() => mx.set(0)}
      aria-labelledby="hero-heading"
      data-testid="hero-section"
      className="ag-grain relative flex min-h-[100svh] items-center justify-center overflow-hidden px-5 pb-20 pt-32 sm:px-8"
    >
      <HeroDecor mouse={{ x, xInv }} />
      <motion.div
        style={{ rotateY: rotY, transformPerspective: 1200 }}
        className="relative z-10 mx-auto flex max-w-4xl flex-col items-center text-center"
      >
        <motion.span
          {...fadeUp(0.1)}
          data-testid="hero-badge"
          className="inline-flex items-center gap-2 rounded-full border border-[#16803C]/25 bg-[#F2FAF4] px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#0B5028] shadow-sm sm:text-xs"
        >
          <Sprout className="h-4 w-4 text-[#16803C]" aria-hidden="true" />
          Something New Is Growing
        </motion.span>

        <h1
          id="hero-heading"
          data-testid="hero-heading"
          className="mt-8 text-[2.3rem] font-extrabold leading-[1.05] tracking-[-0.035em] text-[#171717] sm:text-6xl lg:text-7xl"
        >
          <MaskLine delay={0.25} reduce={reduce}>A fresh new AgriYuvaa</MaskLine>
          <MaskLine delay={0.4} reduce={reduce}>
            experience is{" "}
            <span className="relative inline-block text-[#16803C]">
              coming soon
              <motion.svg
                viewBox="0 0 300 20"
                className="absolute -bottom-1 left-0 h-3 w-full sm:-bottom-2 sm:h-4"
                aria-hidden="true"
              >
                <motion.path
                  d="M4 14C70 4 150 2 296 10"
                  stroke="#16803C"
                  strokeOpacity="0.35"
                  strokeWidth="5"
                  strokeLinecap="round"
                  fill="none"
                  initial={reduce ? false : { pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.2, delay: 1.2, ease: EASE }}
                />
              </motion.svg>
            </span>
          </MaskLine>
        </h1>

        <motion.p
          {...fadeUp(0.75)}
          data-testid="hero-description"
          className="mt-8 max-w-2xl text-base leading-relaxed text-[#555555] sm:text-lg"
        >
          We're building a better AgriYuvaa experience for the next generation of agriculture. Our new
          website is coming soon. Explore agriculture jobs in the meantime!
        </motion.p>

        <motion.div {...fadeUp(1.05)} className="mt-10 flex flex-col items-center">
          <motion.a
            href={JOB_PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-button"
            whileHover={{ y: -3 }}
            whileTap={{ scale: 0.97 }}
            className="ag-focus group relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-[#16803C] py-2 pl-7 pr-2 text-base font-semibold text-white shadow-[0_14px_40px_-12px_rgba(22,128,60,0.6)] transition-[background-color,box-shadow] duration-300 hover:bg-[#0B5028] hover:shadow-[0_20px_50px_-12px_rgba(11,80,40,0.65)] sm:text-lg"
          >
            Explore Agriculture Jobs
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#16803C] transition-transform duration-500 group-hover:rotate-45">
              <ArrowUpRight className="h-5 w-5" aria-hidden="true" />
            </span>
          </motion.a>
          <p data-testid="hero-cta-subtext" className="mt-4 text-xs font-medium text-[#666666] sm:text-sm">
            Discover agriculture jobs, internships &amp; career opportunities.
          </p>
        </motion.div>
      </motion.div>
    </section>
  );
};
